import apiProxy from '@/app/features/shared/utils/apiProxy';
import { AppDispatch } from '@/app/store';
import { 
  setThemeMode,
  setColorTheme as setSimpleColorTheme,
  setLoading as setSimpleLoading
} from '@/app/store/slices/themeSlice';
import { ThemeMode, ColorTheme } from '@/app/features/theme/types/core/themeTypes';
import { updateHtmlAttributes } from '@/app/features/theme/utils/simpleThemeUtils';
import { logger } from '@/app/utils/logger';
import * as coreConstants from '@/app/constants/coreConstants';
import * as apiConstants from '@/app/constants/apiConstants';

/**
 * Fetch user's theme preference from the backend
 * @param userId - The user ID to fetch theme preference for
 */
export const fetchUserThemePreferences = (userId: number) => async (dispatch: AppDispatch) => {
  dispatch(setSimpleLoading(true));
  
  try {
    const params = new URLSearchParams();
    params.append('userId', userId.toString());
    const queryString = params.toString();
    const url = `${apiConstants.API_BASE_URL}${apiConstants.API_PREFERENCES_THEME}?${queryString}`;
    
    logger.apiCall('GET', url, { userId });
    
    const response = await apiProxy({
      method: 'GET',
      url: url,
    });
    
    logger.apiResponse('GET', url, response.status, { userId });
    
    const result = response.data;
    
    if (result.succeeded && result.data && result.data.preference) {
      const themeMode = result.data.preference.themePreference as ThemeMode;
      const colorTheme = result.data.preference.colorThemePreference as ColorTheme;
      
      logger.preferenceLoad('Theme', userId, true, { themeMode, colorTheme });
      
      // Apply to Redux store first
      dispatch(setThemeMode(themeMode));
      dispatch(setSimpleColorTheme(colorTheme));
      
      // Wait for Redux state to settle, then apply to DOM
      await new Promise(resolve => setTimeout(resolve, 50));
      
      console.log('Applying user theme to DOM:', { themeMode, colorTheme });
      updateHtmlAttributes(themeMode, colorTheme);
      
      // Ensure DOM update completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('Theme applied to DOM successfully');
      
      dispatch(setSimpleLoading(false));
      
      return { themeMode, colorTheme };
    } else {
      // If no preference found, use defaults
      logger.info('No theme preference found, using defaults', { userId });
      const defaultTheme: ThemeMode = 'system';
      const defaultColor: ColorTheme = 'purple';
      
      dispatch(setThemeMode(defaultTheme));
      dispatch(setSimpleColorTheme(defaultColor));
      updateHtmlAttributes(defaultTheme, defaultColor);
      dispatch(setSimpleLoading(false));
      
      return { themeMode: defaultTheme, colorTheme: defaultColor };
    }
  } catch (error: any) {
    logger.error('Failed to fetch theme preference', error, { userId });
    
    // Fallback to default theme on error
    const fallbackTheme: ThemeMode = 'system';
    const fallbackColor: ColorTheme = 'purple';
    
    dispatch(setThemeMode(fallbackTheme));
    dispatch(setSimpleColorTheme(fallbackColor));
    updateHtmlAttributes(fallbackTheme, fallbackColor);
    dispatch(setSimpleLoading(false));
    
    logger.preferenceLoad('Theme', userId, false, { fallbackTheme, fallbackColor });
    
    return { themeMode: fallbackTheme, colorTheme: fallbackColor };
  }
};

/**
 * Update user's theme preference in the backend with retry logic
 * @param userId - The user ID
 * @param themeMode - The theme mode to set
 * @param colorTheme - The color theme to set
 * @param partitionKey - The partition key (defaults to SmartMatrix)
 * @param retryCount - Number of retries (defaults to 2)
 */
export const updateUserThemePreferences = (
  userId: number,
  themeMode: ThemeMode,
  colorTheme: ColorTheme,
  partitionKey: string = coreConstants.PARTITION_KEY,
  retryCount: number = 2
) => async (dispatch: AppDispatch) => {
  dispatch(setSimpleLoading(true));
  
  const attemptUpdate = async (attemptsLeft: number): Promise<void> => {
    try {
      const url = `${apiConstants.API_BASE_URL}${apiConstants.API_PREFERENCES_THEME}`;
      
      const requestBody = {
        partitionKey: partitionKey,
        userId: userId,
        themePreference: themeMode,
        colorThemePreference: colorTheme,
      };
      
      logger.apiCall('PUT', url, { userId, themeMode, colorTheme, attemptsLeft });
      
      const response = await apiProxy({
        method: 'PUT',
        url: url,
        data: requestBody,
      });
      
      logger.apiResponse('PUT', url, response.status, { userId });
      
      const result = response.data;
      
      if (result.succeeded && result.data && result.data.preference) {
        const updatedThemeMode = result.data.preference.themePreference as ThemeMode;
        const updatedColorTheme = result.data.preference.colorThemePreference as ColorTheme;
        
        logger.userAction('Theme preference updated', userId, { updatedThemeMode, updatedColorTheme });
        
        // Apply the updated theme sequentially
        dispatch(setThemeMode(updatedThemeMode));
        dispatch(setSimpleColorTheme(updatedColorTheme));
        
        // Wait for Redux state to settle
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Apply to DOM
        updateHtmlAttributes(updatedThemeMode, updatedColorTheme);
        
        // Ensure completion
        await new Promise(resolve => setTimeout(resolve, 50));
        dispatch(setSimpleLoading(false));
      } else {
        const errorMessage = result.messages?.join(', ') || 'Failed to update theme preference';
        logger.warn('Theme update failed', { userId, errorMessage });
        
        // Retry if attempts left
        if (attemptsLeft > 0) {
          logger.info(`Retrying theme update (${attemptsLeft} attempts left)`, { userId });
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
          return attemptUpdate(attemptsLeft - 1);
        }
        
        dispatch(setSimpleLoading(false));
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      logger.error('Theme update exception', error, { userId, attemptsLeft });
      
      // Retry if attempts left
      if (attemptsLeft > 0) {
        logger.info(`Retrying theme update after error (${attemptsLeft} attempts left)`, { userId });
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
        return attemptUpdate(attemptsLeft - 1);
      }
      
      const errorMessage = error?.response?.data?.messages?.join(', ') || 'Failed to update theme preference';
      dispatch(setSimpleLoading(false));
      throw new Error(errorMessage);
    }
  };
  
  await attemptUpdate(retryCount);
};

/**
 * Create async thunk versions for better Redux integration
 */
import { createAsyncThunk } from '@reduxjs/toolkit';

export const fetchUserThemePreferencesThunk = createAsyncThunk(
  'theme/fetchUserThemePreferences',
  async (userId: number, { dispatch }) => {
    return await fetchUserThemePreferences(userId)(dispatch as AppDispatch);
  }
);

export const updateUserThemePreferencesThunk = createAsyncThunk(
  'theme/updateUserThemePreferences',
  async (
    { userId, themeMode, colorTheme, partitionKey }: {
      userId: number;
      themeMode: ThemeMode;
      colorTheme: ColorTheme;
      partitionKey?: string;
    },
    { dispatch }
  ) => {
    await updateUserThemePreferences(userId, themeMode, colorTheme, partitionKey)(dispatch as AppDispatch);
    return { themeMode, colorTheme };
  }
);